import React, { useState, useMemo, useRef } from 'react';
import { bulkUpdatePrices } from '../api';
import AiRatePopover from './AiRatePopover';
import { REASON_CODES, REASON_LABELS, reasonLabel, feedbackLine } from '../utils/aiFeedback';

const errText = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

// Review every pending AI suggestion for one event in a checkbox table and
// apply the selected ones in a single bulk-price call. Per-item results
// render in place (green ✓ old→new, or the guardrail error text from the
// server) and the modal stays open until closed so failures can't be missed.
// Per row: × dismisses with a reason (inline select), and admins get ★ Rate
// (feedback only — never a Skybox write unless they pick "Save & apply").
// "Dismiss unchecked (N)" clears the rest in one go after a confirmation.
//
// Props: suggestions (pending + stale), latestByInventory ({ [invId]: newest
//        suggestion, any status } — may be absent), myInventory (route shape),
//        isAdmin, onClose(), onApplied({ results, errors }) — parent patches
//        caches, onDismiss(sug, reasonCode|null) → Promise,
//        onRate(sug, payload) → Promise, onApplySuggestion(sug, price) →
//        Promise<{ ok, oldPrice, newPrice } | { ok: false, error }>
function AiBulkReviewModal({
  suggestions, latestByInventory, myInventory, isAdmin,
  onClose, onApplied, onDismiss, onRate, onApplySuggestion,
}) {
  // Rows are frozen at first sight. Applied / dismissed suggestions leave the
  // live `suggestions` list (it carries only pending + stale), but their row,
  // result and rating stay here until the modal closes. Order and the
  // listing snapshot (Current B, Δ) are fixed too, so rows don't jump after an
  // apply and the stale-price guard compares against what the table showed.
  const frozenRef = useRef(null);
  const rows = useMemo(() => {
    const inv = new Map((myInventory || []).map(l => [String(l.id), l]));
    const build = (s, prevL) => {
      const l = prevL && prevL.id != null ? prevL : (inv.get(String(s.inventoryId)) || {});
      const delta = l.listPrice ? ((s.suggestedPrice - l.listPrice) / l.listPrice) * 100 : null;
      const initial = !(l.listPrice > 0) || (s.flagCodes || []).includes('INITIAL_PRICE');
      return { s, l, delta, initial };
    };
    if (!frozenRef.current) {
      const first = (suggestions || [])
        .filter(s => s.action !== 'hold')
        .map(s => build(s))
        // first prices on top (they're the reason the table opened), then by size of move
        .sort((a, b) => (b.initial - a.initial) || Math.abs(b.delta ?? 0) - Math.abs(a.delta ?? 0));
      frozenRef.current = new Map(first.map(r => [r.s.id, r]));
    }
    const frozen = frozenRef.current;
    for (const s of suggestions || []) {
      if (s.action === 'hold') continue;
      frozen.set(s.id, build(s, frozen.get(s.id)?.l)); // Map.set keeps an existing key's position
    }
    const liveIds = new Set((suggestions || []).map(s => s.id));
    return [...frozen.values()].map(r => {
      const isLive = liveIds.has(r.s.id);
      // Acted-on suggestions: status + feedback from latestByInventory when it has them
      const latest = latestByInventory?.[String(r.s.inventoryId)];
      const view = !isLive && latest && latest.id === r.s.id ? { ...r.s, ...latest } : r.s;
      return { ...r, view, isLive };
    });
  }, [suggestions, myInventory, latestByInventory]);

  const [checked, setChecked] = useState(() => new Set(
    rows.filter(r => r.s.confidence !== 'low' && r.s.status !== 'stale').map(r => r.s.id)
  ));
  const [applying, setApplying] = useState(false);
  // suggestionId -> { ok, oldPrice, newPrice } | { ok:false, error } | { dismissing } | { dismissed, reasonCode }
  const [results, setResults] = useState({});
  const [globalError, setGlobalError] = useState('');
  const [dismissingId, setDismissingId] = useState(null); // row showing the inline reason select
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkProgress, setBulkProgress] = useState(null); // { done, total }
  const [ratePop, setRatePop] = useState(null); // { id, anchorEl } — row resolved on each render

  // Ratings saved in this modal, so "rated ✓" shows even for a row whose
  // suggestion has left the live list (latestByInventory may be absent)
  const [localRatings, setLocalRatings] = useState({});
  const mineFor = (r) => r.view.feedback?.mine || localRatings[r.s.id] || null;

  const setResult = (id, value) => setResults(prev => ({ ...prev, [id]: value }));
  const uncheck = (id) => setChecked(prev => { const n = new Set(prev); n.delete(id); return n; });

  // Still pending/stale and not acted on in this modal
  const actionable = (r) => {
    const res = results[r.s.id];
    return r.isLive && !res?.ok && !res?.dismissed && !res?.dismissing;
  };
  const busy = applying || !!bulkProgress;

  const actionableRows = rows.filter(actionable);
  const rateRow = ratePop ? rows.find(r => r.s.id === ratePop.id) || null : null;
  // What the popover prefills from: the server's feedback when the row carries
  // it, else a rating saved in this modal grafted on as feedback.mine
  const rateSuggestion = (() => {
    if (!rateRow) return null;
    const localMine = mineFor(rateRow);
    if (rateRow.view.feedback?.mine || !localMine) return rateRow.view;
    return { ...rateRow.view, feedback: { ...(rateRow.view.feedback || {}), mine: localMine } };
  })();
  const selected = actionableRows.filter(r => checked.has(r.s.id));
  const unchecked = actionableRows.filter(r => !checked.has(r.s.id));
  const allChecked = actionableRows.length > 0 && actionableRows.every(r => checked.has(r.s.id));

  const toggle = (id) => setChecked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => {
    if (allChecked) setChecked(new Set());
    else setChecked(new Set(actionableRows.map(r => r.s.id)));
  };

  const apply = async () => {
    if (!selected.length) return;
    const batch = selected.slice(0, 50);
    setApplying(true); setGlobalError('');
    try {
      const res = await bulkUpdatePrices(batch.map(r => ({
        inventoryId: r.s.inventoryId, newPrice: r.s.suggestedPrice, suggestionId: r.s.id,
        // Stale-price guard: 409 per row if the price moved since this table
        // was rendered (e.g. a re-apply after a timed-out batch that landed)
        ...(r.l ? { expectedCurrentPrice: Number(r.l.listPrice) || 0 } : {}),
      })));
      const next = { ...results };
      const byInv = new Map(batch.map(r => [String(r.s.inventoryId), r.s.id]));
      for (const ok of res.data.results || []) {
        const sid = byInv.get(String(ok.inventoryId));
        if (sid != null) next[sid] = { ok: true, oldPrice: ok.oldPrice, newPrice: ok.newPrice };
      }
      for (const bad of res.data.errors || []) {
        const sid = byInv.get(String(bad.inventoryId));
        if (sid != null) next[sid] = { ok: false, error: bad.error };
      }
      setResults(next);
      onApplied?.(res.data);
      if (selected.length > 50) setGlobalError(`Applied the first 50; ${selected.length - 50} remain selected — click Apply again.`);
    } catch (err) {
      setGlobalError(errText(err, 'Bulk apply failed'));
    } finally {
      setApplying(false);
    }
  };

  // The server answers with the row's real status: a suggestion accepted or
  // superseded meanwhile is not dismissed, and says so
  const dismissOutcome = (res, reasonCode) => {
    const st = res?.data?.status;
    return st && st !== 'dismissed'
      ? { ok: false, error: `Not dismissed — already ${st}` }
      : { dismissed: true, reasonCode };
  };

  // Per-row ×: dismiss with the reason picked in the inline select
  const dismissRow = async (r, value) => {
    const reasonCode = value && value !== '__none' ? value : null;
    setDismissingId(null);
    setResult(r.s.id, { dismissing: true });
    try {
      setResult(r.s.id, dismissOutcome(await onDismiss(r.s, reasonCode), reasonCode));
      uncheck(r.s.id);
    } catch (err) {
      setResult(r.s.id, { ok: false, error: `Dismiss failed: ${errText(err, 'request failed')}` });
    }
  };

  // "Dismiss unchecked (N)" after the inline confirmation. One call per row,
  // in order; the first failure stops the run so a dead session or a rate
  // limit isn't hammered N times.
  const runBulkDismiss = async () => {
    const targets = unchecked;
    if (!targets.length) return;
    const reasonCode = bulkReason || null;
    setConfirmBulk(false); setGlobalError('');
    setBulkProgress({ done: 0, total: targets.length });
    for (let i = 0; i < targets.length; i++) {
      const r = targets[i];
      setResult(r.s.id, { dismissing: true });
      try {
        setResult(r.s.id, dismissOutcome(await onDismiss(r.s, reasonCode), reasonCode));
      } catch (err) {
        setResult(r.s.id, { ok: false, error: `Dismiss failed: ${errText(err, 'request failed')}` });
        setGlobalError(`Stopped after an error — ${targets.length - i - 1} unchecked suggestion${targets.length - i - 1 === 1 ? '' : 's'} not dismissed.`);
        break;
      }
      setBulkProgress({ done: i + 1, total: targets.length });
    }
    setBulkProgress(null);
  };

  // Rate popover: Save records feedback only; "Save & apply $X" (open rows
  // only) goes through the parent's accept path for this one listing
  const rate = async (r, payload) => {
    const res = await onRate(r.s, payload);
    setLocalRatings(prev => ({ ...prev, [r.s.id]: res?.data?.feedback || { ...payload } }));
  };
  const saveAndApply = async (payload, price) => {
    const r = rateRow;
    await rate(r, payload);
    const res = await onApplySuggestion(r.s, price);
    if (res?.ok) {
      setResult(r.s.id, { ok: true, oldPrice: res.oldPrice, newPrice: res.newPrice });
      uncheck(r.s.id);
      return;
    }
    const msg = res?.error || 'Not applied';
    setResult(r.s.id, { ok: false, error: msg });
    throw new Error(`Rating saved, but the price was not applied: ${msg}`);
  };

  const fmt = (v) => v == null ? '-' : `$${Number(v).toFixed(2)}`;
  const totalDelta = selected.reduce((s, r) => s + ((r.s.suggestedPrice - (r.l.listPrice || 0)) * (r.l.quantity || 1)), 0);

  const resultCell = (r) => {
    const { s } = r;
    const res = results[s.id];
    if (dismissingId === s.id) {
      return (
        <span className="bulk-dismiss-inline">
          <select defaultValue="" onChange={e => dismissRow(r, e.target.value)} aria-label="Why dismiss?">
            <option value="" disabled>Why dismiss?</option>
            {REASON_CODES.map(c => <option key={c} value={c}>{REASON_LABELS[c]}</option>)}
            <option value="__none">No reason</option>
          </select>
          <button type="button" className="btn-link" onClick={() => setDismissingId(null)}>Keep</button>
        </span>
      );
    }
    if (res?.ok) return <span style={{ color: '#7ee787' }}>✓ {fmt(res.oldPrice)} → {fmt(res.newPrice)}</span>;
    if (res?.dismissing) return <span className="ai-admin-note">Dismissing…</span>;
    if (res?.dismissed) return <span className="ai-admin-note">Dismissed{res.reasonCode ? ` · ${reasonLabel(res.reasonCode)}` : ''}</span>;
    if (res && res.error) return <span style={{ color: '#ff8a8a' }}>{res.error}</span>;
    if (!r.isLive) {
      const st = r.view.status && r.view.status !== 'pending' && r.view.status !== 'stale' ? r.view.status : 'no longer pending';
      return <span className="ai-admin-note">{st}{r.view.dismissReason ? ` · ${reasonLabel(r.view.dismissReason)}` : ''}</span>;
    }
    return null;
  };

  return (
    <div className="modal-overlay" onClick={busy ? undefined : onClose}>
      <div className="modal modal-content" style={{ maxWidth: 1040, width: '95%' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>Review AI suggestions ({rows.length})</h2>
          <button className="modal-close" onClick={onClose} disabled={busy}>×</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '65vh', overflow: 'auto' }}>
          {isAdmin && rows.length > 0 && (
            <div className="ai-admin-note" style={{ marginBottom: 8 }}>
              ★ Rate records right / wrong and the price you'd use — it never writes to Skybox (only "Save &amp; apply" does).
            </div>
          )}
          {rows.length === 0 ? (
            <div className="ai-summary-empty" style={{ padding: 12 }}>No price changes suggested — every listing is a hold.</div>
          ) : (
            <table className="bulk-review-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ width: 28 }}><input type="checkbox" checked={allChecked} disabled={busy || !actionableRows.length} onChange={toggleAll} /></th>
                  <th>Section</th><th>Row</th><th>Qty</th><th>Current B</th><th>Suggested</th><th>Δ</th><th>Conf</th><th>Result</th>
                  <th aria-label="Row actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const { s, l, delta, initial, view } = r;
                  const canAct = actionable(r);
                  const mine = mineFor(r);
                  return (
                    <tr key={s.id} title={s.reasoning} style={{ opacity: canAct ? 1 : 0.7 }}>
                      <td><input type="checkbox" checked={checked.has(s.id) && canAct} disabled={!canAct || busy} onChange={() => toggle(s.id)} /></td>
                      <td>
                        {l.displayName || l.section || '-'}
                        {s.status === 'stale' ? <span className="ai-chip ai-chip-stale" style={{ marginLeft: 6 }}>STALE</span> : null}
                        {initial ? <span className="ai-chip ai-chip-priority p-med" style={{ marginLeft: 6 }} title="First price for an unpriced listing">NEW</span> : null}
                      </td>
                      <td>{l.row || '-'}</td>
                      <td>{l.quantity ?? '-'}</td>
                      <td>{l.listPrice > 0 ? fmt(l.listPrice) : <span title="Unpriced — not on any marketplace yet">unpriced</span>}</td>
                      <td className={`suggest-price act-${s.action}`} style={{ cursor: 'default' }}>{fmt(s.suggestedPrice)}</td>
                      <td style={{ color: delta < 0 ? '#ff8a8a' : '#7ee787' }}>{delta == null ? (initial ? 'first' : '-') : `${delta > 0 ? '+' : ''}${delta.toFixed(1)}%`}</td>
                      <td><span className={`conf-chip conf-${s.confidence}`} style={{ cursor: 'default' }}>{(s.confidence || '?')[0].toUpperCase()}</span></td>
                      <td className="bulk-result-cell">{resultCell(r)}</td>
                      <td className="bulk-row-actions">
                        {isAdmin && (
                          <button
                            type="button"
                            className={`suggest-rate-btn${mine ? ' rated' : ''}`}
                            title={mine ? `Your rating: ${feedbackLine(mine)} — click to edit (never writes to Skybox)` : 'Rate this suggestion (never writes to Skybox)'}
                            onClick={(e) => setRatePop({ id: s.id, anchorEl: e.currentTarget })}
                          >
                            {mine ? 'rated ✓' : '★ Rate'}
                          </button>
                        )}
                        {canAct && dismissingId !== s.id && (
                          <button
                            type="button"
                            className="suggest-dismiss-btn"
                            title="Dismiss this suggestion (asks why)"
                            disabled={busy}
                            onClick={() => setDismissingId(s.id)}
                          >×</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {globalError && <div className="ai-inline-error" style={{ marginTop: 10 }}>{globalError}</div>}
        </div>
        <div className="modal-footer bulk-review-footer">
          {confirmBulk ? (
            <span className="bulk-dismiss-confirm">
              Dismiss {unchecked.length} unchecked suggestion{unchecked.length === 1 ? '' : 's'}?
              <select value={bulkReason} onChange={e => setBulkReason(e.target.value)} aria-label="Reason for all">
                <option value="">No reason</option>
                {REASON_CODES.map(c => <option key={c} value={c}>{REASON_LABELS[c]}</option>)}
              </select>
              <button className="btn-danger btn-sm" onClick={runBulkDismiss} disabled={!unchecked.length}>Dismiss {unchecked.length}</button>
              <button className="btn-secondary btn-sm" onClick={() => setConfirmBulk(false)}>Cancel</button>
            </span>
          ) : (
            <span className="ai-admin-note">
              {bulkProgress
                ? `Dismissing ${bulkProgress.done}/${bulkProgress.total}…`
                : <>
                    {selected.length} selected{selected.length ? ` · net ${totalDelta >= 0 ? '+' : '-'}$${Math.abs(totalDelta).toFixed(0)} across selected seats` : ''}
                    {selected.length > 50 ? ' · applies 50 at a time' : ''}
                  </>}
            </span>
          )}
          <span style={{ flex: 1 }} />
          {!confirmBulk && unchecked.length > 0 && (
            <button className="btn-secondary" onClick={() => { setBulkReason(''); setConfirmBulk(true); }} disabled={busy}>
              Dismiss unchecked ({unchecked.length})
            </button>
          )}
          <button className="btn-secondary" onClick={onClose} disabled={busy}>Close</button>
          <button className="btn-primary" onClick={apply} disabled={busy || !selected.length}>
            {applying ? 'Applying… (this can take a minute)' : `Apply ${Math.min(selected.length, 50)} selected`}
          </button>
        </div>
      </div>
      {rateRow && (
        <AiRatePopover
          key={rateRow.s.id}
          suggestion={rateSuggestion}
          anchorEl={ratePop.anchorEl}
          isAdmin={isAdmin}
          mode="rate"
          onSave={(payload) => rate(rateRow, payload)}
          onSaveAndApply={actionable(rateRow) && onApplySuggestion ? saveAndApply : undefined}
          onClose={() => setRatePop(null)}
        />
      )}
    </div>
  );
}

export default AiBulkReviewModal;
